import { useCallback, useRef, useState } from 'react';

import { buildCreatePayload } from './utils';

import type { FormikValues } from 'formik';
import type { FormFieldName, SaveField } from '../../../types';
import type { UseAutoSaveRowParams, UseAutoSaveRowResult } from './types';

// Persists a single field the moment the user leaves it. The first valid field on a draft row
// creates the entry and reports its id upward; every field after that patches by id. Saves are
// serialized through a queue so a field saved while the create request is still in flight is
// applied once the entry exists instead of being dropped.
export default function useAutoSaveRow<TValues extends FormikValues, TPayload extends object>({
  rowId,
  formik,
  validationSchema,
  toFieldPayload,
  onCreate,
  onUpdate,
  onCreated,
  onSaved,
}: UseAutoSaveRowParams<TValues, TPayload>): UseAutoSaveRowResult<TValues> {
  const valuesRef = useRef(formik.values);
  valuesRef.current = formik.values;
  const savedValuesRef = useRef(formik.initialValues);
  const rowIdRef = useRef(rowId);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [isCreating, setIsCreating] = useState(false);

  const saveField: SaveField<TValues> = useCallback(
    (name, value) => {
      const performSave = async () => {
        const values: TValues = { ...valuesRef.current };
        if (value !== undefined) {
          values[name] = value;
        }

        try {
          await validationSchema.validateAt(name, values);
        } catch {
          return;
        }

        const data = toFieldPayload(name, values);
        if (JSON.stringify(data) === JSON.stringify(toFieldPayload(name, savedValuesRef.current))) {
          return;
        }

        const currentId = rowIdRef.current;

        if (currentId == null) {
          const { data: createData, names: createdNames } = await buildCreatePayload({
            base: data,
            name,
            values,
            savedValues: savedValuesRef.current,
            validationSchema,
            toFieldPayload,
          });

          let createdId: number | undefined;
          setIsCreating(true);
          try {
            createdId = await onCreate(createData);
          } catch {
            return;
          } finally {
            setIsCreating(false);
          }
          if (createdId == null) {
            return;
          }
          rowIdRef.current = createdId;
          const savedCreatedValues = { ...savedValuesRef.current };
          for (const createdName of createdNames) {
            savedCreatedValues[createdName] = values[createdName];
          }
          savedValuesRef.current = savedCreatedValues;
          onCreated(createdId);
          onSaved();
          return;
        }

        let didUpdate = false;
        try {
          didUpdate = await onUpdate(currentId, data);
        } catch {
          return;
        }
        if (didUpdate) {
          // toFieldPayload can persist fields beyond the triggering one (e.g. a role
          // change also clears caregiver_type), so mark every field it carried as saved.
          const savedUpdatedValues = { ...savedValuesRef.current };
          for (const savedName in values) {
            if (savedName === name || savedName in data) {
              savedUpdatedValues[savedName] = values[savedName];
            }
          }
          savedValuesRef.current = savedUpdatedValues;
          onSaved();
        }
      };

      // performSave never rejects (every await is wrapped), so the chain can't break.
      const queuedSave = saveQueueRef.current.then(performSave);
      saveQueueRef.current = queuedSave;
      return queuedSave;
    },
    [validationSchema, toFieldPayload, onCreate, onUpdate, onCreated, onSaved],
  );

  const getChangedFieldNames = useCallback((values: TValues) => {
    const changedNames: Array<FormFieldName<TValues>> = [];
    for (const name in values) {
      if (JSON.stringify(toFieldPayload(name, values)) !== JSON.stringify(toFieldPayload(name, savedValuesRef.current))) {
        changedNames.push(name);
      }
    }
    return changedNames;
  }, [toFieldPayload]);

  return { saveField, getChangedFieldNames, isCreating };
}
